import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export interface OpportunityBoard {
  id: number;
  name: string;
  color: string;
  organization: number;
  is_default?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface OpportunityBoardStatus {
  id: number;
  board: number;
  name: string;
  order: number;
}

// Fetch all opportunity boards
export function useOpportunityBoards() {
  return useQuery({
    queryKey: ['opportunity-boards'],
    queryFn: async () => {
      const response = await api.get<OpportunityBoard[] | { results: OpportunityBoard[] }>('/api/crm/opportunity-boards/');
      return Array.isArray(response.data) ? response.data : (response.data as any).results || [];
    },
  });
}

// Fetch statuses for a specific board
export function useOpportunityBoardStatuses(boardId: number | null) {
  return useQuery({
    queryKey: ['opportunity-board-statuses', boardId],
    queryFn: async () => {
      if (!boardId) return [];
      const response = await api.get<OpportunityBoardStatus[] | { results: OpportunityBoardStatus[] }>(
        `/api/crm/opportunity-board-statuses/?board=${boardId}`
      );
      const data = Array.isArray(response.data) ? response.data : (response.data as any).results || [];
      // Sort by order
      return data.sort((a: OpportunityBoardStatus, b: OpportunityBoardStatus) => (a.order || 0) - (b.order || 0));
    },
    enabled: !!boardId,
  });
}

// Create a new board
export function useCreateOpportunityBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { name: string; color: string }): Promise<OpportunityBoard> => {
      const response = await api.post<OpportunityBoard>('/api/crm/opportunity-boards/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-boards'] });
    },
  });
}

// Update a board
export function useUpdateOpportunityBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<OpportunityBoard> }) => {
      const response = await api.patch(`/api/crm/opportunity-boards/${id}/`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-boards'] });
    },
  });
}

// Delete a board
export function useDeleteOpportunityBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/crm/opportunity-boards/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-boards'] });
    },
  });
}

// Create a new status
export function useCreateOpportunityBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { board: number; name: string; order: number }): Promise<OpportunityBoardStatus> => {
      const response = await api.post<OpportunityBoardStatus>('/api/crm/opportunity-board-statuses/', data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-board-statuses', variables.board] });
    },
  });
}

// Update a status
export function useUpdateOpportunityBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<OpportunityBoardStatus> }): Promise<OpportunityBoardStatus> => {
      const response = await api.patch<OpportunityBoardStatus>(`/api/crm/opportunity-board-statuses/${id}/`, data);
      return response.data;
    },
    onSuccess: (result: OpportunityBoardStatus) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-board-statuses', result.board] });
    },
  });
}

// Delete a status
export function useDeleteOpportunityBoardStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, boardId }: { id: number; boardId: number }) => {
      await api.delete(`/api/crm/opportunity-board-statuses/${id}/`);
      return boardId;
    },
    onSuccess: (boardId) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-board-statuses', boardId] });
    },
  });
}

// Batch update status orders
export function useUpdateStatusOrders() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ boardId, updates }: { 
      boardId: number; 
      updates: { id: number; order: number }[] 
    }) => {
      await Promise.all(
        updates.map(update =>
          api.patch(`/api/crm/opportunity-board-statuses/${update.id}/`, { order: update.order })
        )
      );
      return boardId;
    },
    onSuccess: (boardId) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity-board-statuses', boardId] });
    },
  });
}